import * as React from "react";
import { Alert } from "../../../components/ui/alert";
import { Button } from "../../../components/ui/button";
import { Progress } from "../../../components/ui/feedback";
import { Input } from "../../../components/ui/input";
import { CloudCheckIcon } from "../../../components/icons";
import PaneStatus from "../../../components/feedback/PaneStatus";
import { FieldLabel } from "../../../components/forms/FormSection";
import { describeError } from "../../../utils/describeError";
import { CheckOutIdentity, DocumentCheckOutAdapter, useHostAdapter } from "../../../platform";
import { forgetCheckOutIdentity } from "../../../platform/shared/identityOnce";
import { readDocumentUrl, readOfficePlatform } from "../../../platform/shared/documentLocation";
import {
  MAX_SINGLE_UPLOAD_BYTES,
  ProjectSummary,
  useUploadDocumentMutation,
} from "../../filing/api/filingApi";
import ProjectSearchList from "../../filing/components/ProjectSearchList";
import { useCheckOutDocumentMutation } from "../api/checkoutApi";
import { checkOutDeviceLabel, defaultDocumentName, uploadFileName } from "../utils/newDocument";

// Save to Jylo for a document that did not come from Jylo: choose a project and a name,
// and the open document is uploaded, checked out, and stamped as that check out. From
// then on it is indistinguishable from a document opened out of Jylo -- the header's
// Save and Check in work on it, and nothing here is needed again.
//
// Each step is remembered once it succeeds, so Try again resumes rather than restarts.
// Re-running the upload after a failed check out would leave a second copy in the
// project, so once the file is up the project and name are fixed.

type Step = "reading" | "uploading" | "checking-out" | "linking";

const STEP_LABELS: Record<Step, string> = {
  reading: "Reading the document…",
  uploading: "Uploading to Jylo…",
  "checking-out": "Checking it out to you…",
  linking: "Linking this document to Jylo…",
};

type Phase =
  | { kind: "choosing" }
  | { kind: "working"; step: Step }
  | { kind: "saved" }
  | { kind: "failed"; step: Step; detail: string };

interface Uploaded {
  projectObjectIdentifier: string;
  projectName: string;
  documentObjectIdentifier: string;
  fileName: string;
}

export interface SaveToJyloViewProps {
  /** Passed in rather than read from context: a host without one never renders this. */
  documents: DocumentCheckOutAdapter;
  onClose: () => void;
  /** The document now carries its stamp; whoever verified it as "not in Jylo" must look again. */
  onLinked: () => void;
  /** True while a step runs, so the sheet is not closed from under it. */
  onBusyChange?: (busy: boolean) => void;
}

/** What the user is told when a step fails, before the detail. */
const failureLead = (step: Step, uploaded: Uploaded | null): string => {
  switch (step) {
    case "reading":
    case "uploading":
      return "The document was not saved to Jylo.";
    case "checking-out":
      return `The document is in ${uploaded?.projectName ?? "the project"}, but could not be checked out to you, so later saves would not reach it.`;
    case "linking":
      return "The document is in Jylo and checked out to you, but this copy could not be linked to it.";
  }
};

const SaveToJyloView = ({ documents, onClose, onLinked, onBusyChange }: SaveToJyloViewProps) => {
  const host = useHostAdapter();
  const [uploadDocument] = useUploadDocumentMutation();
  const [checkOutDocument] = useCheckOutDocumentMutation();

  const [name, setName] = React.useState(() => defaultDocumentName(readDocumentUrl()));
  const [selected, setSelected] = React.useState<ProjectSummary | null>(null);
  const [phase, setPhase] = React.useState<Phase>({ kind: "choosing" });
  const [progress, setProgress] = React.useState<number | null>(null);

  // The steps already done, kept across a retry. Refs, not state: nothing renders from
  // them that is not also in `phase`, and a retry must read them synchronously.
  const uploadedRef = React.useRef<Uploaded | null>(null);
  const identityRef = React.useRef<CheckOutIdentity | null>(null);

  const fileName = uploadFileName(name, documents.fileExtension);

  const busy = phase.kind === "working";
  React.useEffect(() => {
    onBusyChange?.(busy);
  }, [busy, onBusyChange]);

  // Once the file is up, its project and name are history, not choices.
  const committed = uploadedRef.current !== null;
  const locked = busy || committed;

  const runSave = async () => {
    if (!uploadedRef.current && (!selected || !fileName)) return;
    let step: Step = "reading";
    try {
      if (!uploadedRef.current && selected && fileName) {
        setPhase({ kind: "working", step });
        const content = await documents.getDocumentBytes(setProgress);
        setProgress(null);
        if (content.size > MAX_SINGLE_UPLOAD_BYTES) {
          throw new Error(
            `This document is ${Math.ceil(content.size / (1024 * 1024))} MB. Documents over ` +
              `${MAX_SINGLE_UPLOAD_BYTES / (1024 * 1024)} MB cannot be saved from ${host.displayName} yet; upload it in Jylo instead.`
          );
        }

        step = "uploading";
        setPhase({ kind: "working", step });
        const created = await uploadDocument({
          projectObjectIdentifier: selected.objectIdentifier,
          file: content,
          fileName,
          metadata: { source: `${host.kind}-addin` },
          uploadName: `Saved from ${host.displayName}`,
        }).unwrap();
        uploadedRef.current = {
          projectObjectIdentifier: selected.objectIdentifier,
          projectName: selected.name,
          documentObjectIdentifier: created.objectIdentifier,
          fileName: created.name || fileName,
        };
      }
      const uploaded = uploadedRef.current!;

      if (!identityRef.current) {
        step = "checking-out";
        setPhase({ kind: "working", step });
        identityRef.current = await checkOutDocument({
          projectObjectIdentifier: uploaded.projectObjectIdentifier,
          documentObjectIdentifier: uploaded.documentObjectIdentifier,
          deviceLabel: checkOutDeviceLabel(host.displayName, readOfficePlatform()),
        }).unwrap();
      }

      step = "linking";
      setPhase({ kind: "working", step });
      await documents.writeCheckOutIdentity(identityRef.current);
    } catch (error) {
      setProgress(null);
      setPhase({ kind: "failed", step, detail: describeError(error) });
      return;
    }

    // Outside the try: the stamp is written, so nothing after this point may report the
    // link as failed -- a retry would only write a second stamp.
    // The cache still holds "no stamp" from the pane's first read.
    forgetCheckOutIdentity();
    setPhase({ kind: "saved" });
    try {
      onLinked();
    } catch (error) {
      console.warn("Saved to Jylo, but the header did not re-check the document", error);
    }
  };

  // ── Terminal and blocking states ─────────────────────────────────────────────

  const unavailable = documents.describeSaveUnavailable();
  if (unavailable) {
    return (
      <div className="px-4 pb-4">
        <Alert severity="info">{unavailable}</Alert>
      </div>
    );
  }

  if (phase.kind === "saved" && uploadedRef.current) {
    return (
      <PaneStatus
        compact
        icon={<CloudCheckIcon className="size-7 text-success" />}
        title={`Saved to ${uploadedRef.current.projectName}`}
        description={`Saved as ${uploadedRef.current.fileName} and checked out to you. Save keeps Jylo up to date as you work; Check in publishes it as a new version.`}
        action={
          <Button size="lg" onClick={onClose}>
            Done
          </Button>
        }
      />
    );
  }

  // ── The form ─────────────────────────────────────────────────────────────────

  return (
    <div className="flex min-h-0 flex-col gap-3.5 px-4 pb-4">
      <div className="min-w-0">
        <h2 className="text-h6 text-strong">Save to Jylo</h2>
        <div className="text-caption text-muted-foreground">
          This document is not in Jylo yet. Choose where it goes; it is then checked out to you, so
          your later saves go to the same place.
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <FieldLabel htmlFor="save-to-jylo-name">Name</FieldLabel>
        <div className="flex flex-row items-center gap-1.5">
          <Input
            id="save-to-jylo-name"
            placeholder="Document name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={locked}
          />
          <span className="flex-none text-body2 text-muted-foreground">
            .{documents.fileExtension}
          </span>
        </div>
      </div>

      <ProjectSearchList
        id="save-to-jylo-project"
        label="Project"
        selected={selected}
        onSelect={setSelected}
        disabled={locked}
      />

      {phase.kind === "working" && (
        <div className="flex flex-col gap-1.5">
          <p className="text-body2">{STEP_LABELS[phase.step]}</p>
          <Progress
            value={phase.step === "reading" && progress !== null ? progress * 100 : undefined}
          />
        </div>
      )}

      {phase.kind === "failed" && (
        <Alert severity="error">
          {failureLead(phase.step, uploadedRef.current)}
          <div className="mt-1 text-caption break-words">{phase.detail}</div>
        </Alert>
      )}

      <div className="flex flex-row justify-end gap-2">
        <Button variant="ghost-primary" size="lg" onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button
          size="lg"
          onClick={() => void runSave()}
          disabled={busy || (!committed && (!selected || !fileName))}
        >
          {busy ? "Saving…" : phase.kind === "failed" ? "Try again" : "Save"}
        </Button>
      </div>
    </div>
  );
};

export default SaveToJyloView;
